import React, { useEffect, useState } from "react";
import _ from "lodash";
import localStorage from "localStorage";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  UnfoldMore,
} from "@mui/icons-material";
import { getTranslation } from "utils/renderLanguage";

import "./index.scss";

const PaginatedTable = ({
  className,
  style,
  hidePagination,
  rowsPerPage = 10,

  rowData,
  columnData,
  actionsColumn,
}) => {
  const language = localStorage.getItem("english");
  const [viewPage, setViewPage] = useState(1);
  const [pageNumber, setPageNumber] = useState([1]);
  const [displayData, setDisplayData] = useState([]);
  const [sortedColumn, setSortedColumn] = useState({});

  useEffect(() => {
    let temp = _.cloneDeep(rowData);
    let page_number = 1;
    if (rowData.length > rowsPerPage && !hidePagination) {
      page_number = Math.floor(rowData.length / rowsPerPage);
      if (rowData.length % rowsPerPage > 0) {
        page_number++;
      }
      temp.splice(rowsPerPage, rowData.length);
    }
    setPageNumber(Array.from({ length: page_number }, (x, i) => i + 1));
    setDisplayData(temp);
  }, [rowData, rowsPerPage]);

  useEffect(() => {
    let temp = _.cloneDeep(rowData);
    if (!hidePagination) {
      let startIndex = rowsPerPage * (viewPage - 1);
      temp.splice(0, startIndex);
      temp.splice(startIndex + rowsPerPage, rowData.length);
    }
    setDisplayData(temp);
  }, [viewPage, rowData, rowsPerPage, hidePagination]);

  const onClickSort = (column) => {
    let temp = _.cloneDeep(displayData);
    if (sortedColumn.column === column.accessor) {
      temp = temp.reverse();
      setSortedColumn({
        column: column.accessor,
        order: sortedColumn.order === "asc" ? "desc" : "asc",
      });
    } else {
      temp = temp.sort((a, b) => {
        let aVal = a[column.accessor];
        let bVal = b[column.accessor];

        // Try to convert to numbers if possible for proper numeric sorting
        const aNum = Number(aVal);
        const bNum = Number(bVal);

        // If both are valid numbers, compare as numbers
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortedColumn.order === "asc" ? aNum - bNum : bNum - aNum;
        }

        // Otherwise, compare as strings
        if (aVal < bVal) {
          return sortedColumn.order === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortedColumn.order === "asc" ? 1 : -1;
        }
        return 0;
      });
      setSortedColumn({ column: column.accessor, order: "asc" });
    }
    setDisplayData(temp);
  };

  return (
    <div className={`overflow-auto ${className || ""}`} style={{ ...style }}>
      <div className="paginated-header">
        {columnData.map((item, index) => {
          return (
            <div
              key={index}
              style={{ 
                ...item.containerStyle, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                textAlign: 'center'
              }}
              className={`paginated-table__header-cont`}
            >
              <p className="w-100" style={{ fontSize: '14px', margin: 0, textAlign: 'center' }}>
                {item.header}
              </p>
              {item.accessor && (
                <button
                  className="paginated-table__header-sort-btn"
                  onClick={() => onClickSort(item)}
                >
                  {sortedColumn.column === item.accessor &&
                    sortedColumn.order === "asc" && <ExpandMore />}
                  {sortedColumn.column === item.accessor &&
                    sortedColumn.order === "desc" && <ExpandLess />}
                  {sortedColumn.column !== item.accessor && <UnfoldMore />}
                </button>
              )}
            </div>
          );
        })}
        {actionsColumn && actionsColumn.length > 0 && (
          <div
            style={{ 
              width: `${actionsColumn.length * 25}%`, 
              minWidth: 70,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center'
            }}
            className={`paginated-table__header-cont`}
          >
            <p style={{ fontSize: '14px', margin: 0, textAlign: 'center' }}>
              {getTranslation(language, "Actions")}
            </p>
          </div>
        )}
      </div>
      <div className="paginated-content">
        {displayData &&
          displayData.length > 0 &&
          displayData.map((data, dataIndex) => {
            return (
              <div key={dataIndex} className="paginated-table__content-cont">
                {columnData.map((column, columnIndex) => {
                  return (
                    <div
                      key={columnIndex}
                      style={{ 
                        ...column.containerStyle,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        textAlign: 'center'
                      }}
                      className="paginated-table__content-column"
                    >
                      {column.accessor && !column.renderColumn && (
                        <p style={{ fontSize: '14px', margin: 0, textAlign: 'center', width: '100%' }}>
                          {data[column.accessor] || "-"}
                        </p>
                      )}
                      {column.renderColumn && (
                        <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          {column.renderColumn(data, dataIndex)}
                        </div>
                      )}
                    </div>
                  );
                })}
                {actionsColumn && actionsColumn.length > 0 && (
                  <div
                    style={{
                      width: `${actionsColumn.length * 25}%`,
                      minWidth: 70,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}
                    className={`paginated-table__content-column flex-wrap`}
                  >
                    {actionsColumn.map((item, index) => {
                      if (!item.onShow || (item.onShow && item.onShow(data))) {
                        return (
                          <button
                            key={index}
                            disabled={item.disabled}
                            style={{ ...item.style, fontSize: '14px' }}
                            className={
                              item.renderActionIcon
                                ? "paginated-table__content-action-btn"
                                : `btn ${item.className}`
                            }
                            onClick={() => item.onClick(data, dataIndex)}
                          >
                            {item.renderActionIcon ||
                              getTranslation(language, item.name)}
                          </button>
                        );
                      }
                    })}
                  </div>
                )}
                {!(actionsColumn && actionsColumn.length > 0) && (
                  <p style={{ fontSize: '14px', margin: 0, textAlign: 'center' }}>
                    {getTranslation(language, "No action required")}
                  </p>
                )}
              </div>
            );
          })}
        {displayData && displayData.length === 0 && (
          <div className="paginated-table__content-column" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <h4 className="at-card-list__no-result w-100" style={{ fontSize: '14px', textAlign: 'center' }}>
              {getTranslation(language, "No Result Found")}
            </h4>
          </div>
        )}
      </div>
      {rowData.length > rowsPerPage && !hidePagination && (
        <>
          <div className="paginated-pagination_data-count">
            {getTranslation(language, "Showing from ")}
            <span>{rowsPerPage * (viewPage - 1) + 1}</span> -{" "}
            <span>
              {rowsPerPage * viewPage > rowData.length ? rowData.length : rowsPerPage * viewPage}
            </span>{" "}
            of <span>{rowData.length}</span> results
          </div>
          <div className="paginated-pagination">
            <button
              className="paginated-pagination__button"
              disabled={viewPage === 1}
              onClick={() => setViewPage(viewPage - 1)}
            >
              <ChevronLeft style={{ width: 14, height: 14 }} />
            </button>
            {pageNumber.map((value, index) => {
              return (
                <button
                  key={index}
                  onClick={() => setViewPage(Number(value))}
                  className={`paginated-pagination__button ${
                    viewPage === value &&
                    "paginated-pagination__button-selected"
                  }`}
                >
                  {value}
                </button>
              );
            })}
            <button
              className="paginated-pagination__button"
              disabled={viewPage === pageNumber.length}
              onClick={() => setViewPage(viewPage + 1)}
            >
              <ChevronRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaginatedTable;
